import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import { setCurrentSelectedMultimediaInput } from 'apps/web_forms/slices/multimediaSlice';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';

const getSignAndUnsignedValue = (value) => {
  const stringValue = `${value ?? ''}`;
  const isNegative = stringValue.startsWith('-');

  return {
    sign: isNegative ? '-' : '',
    unsignedValue: isNegative ? stringValue.slice(1) : stringValue,
  };
};

const sanitizeIntegerValue = (value) => {
  const { sign, unsignedValue } = getSignAndUnsignedValue(value);
  const nonDigitRegex = /\D/g;
  const digitsOnly = unsignedValue.replace(nonDigitRegex, '');

  return `${sign}${digitsOnly}`;
};

const sanitizeDecimalValue = (value) => {
  const { sign, unsignedValue } = getSignAndUnsignedValue(value);
  let sanitizedValue = '';
  let hasDecimalSeparator = false;

  [...unsignedValue].forEach((char) => {
    const singleDigitRegex = /\d/;
    if (singleDigitRegex.test(char)) {
      sanitizedValue += char;
    } else if (char === '.' && !hasDecimalSeparator) {
      sanitizedValue += char;
      hasDecimalSeparator = true;
    }
  });

  return `${sign}${sanitizedValue}`;
};

const normalizeIntegerValue = (value) => {
  const sanitizedValue = sanitizeIntegerValue(value);

  if (sanitizedValue === '' || sanitizedValue === '-') {
    return '';
  }

  const { sign, unsignedValue } = getSignAndUnsignedValue(sanitizedValue);
  const redundantLeadingZerosRegex = /^0+(?=\d)/;
  const normalizedDigits = unsignedValue.replace(redundantLeadingZerosRegex, '');
  const standardValue = normalizedDigits || '0';

  return sign && standardValue !== '0'
    ? `${sign}${standardValue}`
    : standardValue;
};

const normalizeDecimalValue = (value) => {
  const sanitizedValue = sanitizeDecimalValue(value);

  if (
    sanitizedValue === '' ||
    sanitizedValue === '-' ||
    sanitizedValue === '.' ||
    sanitizedValue === '-.'
  ) {
    return '';
  }

  const { sign, unsignedValue } = getSignAndUnsignedValue(sanitizedValue);
  const [rawIntegerPart = '', rawFractionPart = ''] = unsignedValue.split('.');
  const redundantLeadingZerosRegex = /^0+(?=\d)/
  const integerPart = rawIntegerPart.replace(redundantLeadingZerosRegex, '') || '0';
  const trailingZerosRegex = /0+$/
  const fractionPart = rawFractionPart.replace(trailingZerosRegex, '');
  const standardValue = fractionPart.length
    ? `${integerPart}.${fractionPart}`
    : integerPart;

  return sign && standardValue !== '0'
    ? `${sign}${standardValue}`
    : standardValue;
};

const sanitizeNumberValue = (value, type) =>
  type === 'integer'
    ? sanitizeIntegerValue(value)
    : sanitizeDecimalValue(value);

const normalizeNumberValue = (value, type) =>
  type === 'integer'
    ? normalizeIntegerValue(value)
    : normalizeDecimalValue(value);

const NumberInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const { identifier, type, title, hint, hidden, validate_msg: validateMsg } = element;
  const { register } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const [val, setVal] = useState(currentValue);
  const [isEditing, setIsEditing] = useState(false);

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));

  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const dispatch = useDispatch();
  const [{ isActive, isElementValid, isRequired, isReadOnly, initialAnswer }] =
    useRules(element, formContext);

  useEffect(() => {
    register(identifier, {
      validate: {
        customValidation: () => isElementValid || validateMsg || false,
        numberTypeValidation: (v) => {
          // No validation on empty fields
          if ((!!v && v.toString().length > 0)) {
            // Integer validation
            if (type === 'integer' && (!Number.isInteger(parseFloat(v)) || isNaN(parseInt(v))) ) {
              return 'This should be an integer number';
            }
          }
          return true;
        },
      },
      required: isRequired ? 'This field is required' : false,
    });
  }, [isElementValid, isRequired]);

  useEffect(() => {
    if (isEditing) {
      return;
    }

    if(initialAnswer == undefined || initialAnswer == null || isNaN(initialAnswer)) {
      setVal('');
      changeValue(''); //Added so that it's empty value can be tracked in progression circle of Group/Subform
      return;
    } else {
      const normalizedInitialAnswer = normalizeNumberValue(initialAnswer, type);
      changeValue(normalizedInitialAnswer);
      setVal(normalizedInitialAnswer);
    }
  }, [initialAnswer, isEditing]);

  const onChange = (value) => {
    const sanitizedValue = sanitizeNumberValue(value, type);

    setVal(sanitizedValue);
    changeValue(normalizeNumberValue(sanitizedValue, type), true);
  };

  const onBlur = () => {
    const normalizedValue = normalizeNumberValue(val, type);

    setVal(normalizedValue);
    changeValue(normalizedValue, true);
    setIsEditing(false);
  };

  const onFocus = () => {
    setIsEditing(true);
    removeResourceUpdateHighlight();
  };

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || hidden || !showContent) return null;

  return (
    <div className='react-form-control'>
      <label htmlFor={identifier}>{title}</label>
      <input
        type='text'
        inputMode={type === 'decimal' ? 'decimal' : 'numeric'}
        id={identifier}
        name={identifier}
        value={val}
        onClick={() => dispatch(setCurrentSelectedMultimediaInput({}))}
        placeholder='Answer'
        className={clsx(
          errorMessage !== undefined && 'invalid',
          hasResourceUpdateHighlight && 'updateHighlight',
          isReadOnly && 'read-only'
        )}
        readOnly={isReadOnly}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      {hint && <p className='element-hint'>{hint}</p>}
      {errorMessage !== undefined && (
        <ErrorMessage id={`${identifier}_errmsg`}>{errorMessage}</ErrorMessage>
      )}
    </div>
  );
};

NumberInput.propTypes = {
  element: PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    initialAnswer: PropTypes.string,
    type: PropTypes.string,
    hint: PropTypes.string,
    hidden: PropTypes.bool,
    validate_msg: PropTypes.string,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(NumberInput);
